import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { lagged, squashStretch, settleScale, breathe, sway } from "./anim";

/**
 * Anivaze v4 — STORYLINE character puppet (STORYLINE.md §2–3).
 *
 * A parametric vector puppet: one skeleton (head / torso / 2 arms / 2 legs) driven
 * by joint angles, plus an attribute space (age / build / height / hair / face /
 * outfit). Deterministic pure-function-of-`t`, so a cast of these costs ~$0 to
 * render (STORYLINE §8). This sub-step ships the STICK family + the reaction
 * library; the avatar / hand-drawn families and the story stage build on it.
 */

const TAU = Math.PI * 2;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

export interface CharacterSpec {
  family: "stick" | "avatar";
  age: "child" | "young" | "adult" | "old";
  build: "slim" | "normal" | "fat";
  height: "short" | "normal" | "tall";
  hair: "bald" | "short" | "curly" | "long" | "hairy";
  hairColor?: string;
  facialHair?: "none" | "stubble" | "beard" | "moustache";
  skinTone?: string;
  face?: { glasses?: boolean; pimple?: boolean; freckles?: boolean };
  outfit?: { top?: string; accent?: string };
}

export const DEFAULT_CHARACTER: CharacterSpec = {
  family: "stick", age: "adult", build: "normal", height: "normal",
  hair: "short", skinTone: "#e8b48c", outfit: { top: "#4a72d0" },
};

/** A few named presets (STORYLINE §2 — the ~40 roster is bundles like these). */
export const CHARACTER_PRESETS: Record<string, CharacterSpec> = {
  student: { ...DEFAULT_CHARACTER, age: "young", hair: "curly", skinTone: "#c98a5e", outfit: { top: "#e0704a" }, face: { glasses: true } },
  teacher: { ...DEFAULT_CHARACTER, age: "adult", hair: "short", facialHair: "beard", skinTone: "#e8b48c", outfit: { top: "#3aa06a" } },
  elder: { ...DEFAULT_CHARACTER, age: "old", build: "normal", hair: "bald", facialHair: "moustache", skinTone: "#d8a074", outfit: { top: "#8a6fb0" } },
  kid: { ...DEFAULT_CHARACTER, age: "child", height: "short", hair: "short", skinTone: "#f0c39a", outfit: { top: "#f0b13a" } },
};

export type ReactionName =
  | "idle" | "talk" | "happy" | "laugh" | "sad" | "angry" | "shocked"
  | "think" | "point" | "wave" | "shrug" | "thumbsup" | "celebrate" | "nod";

export const REACTIONS: ReactionName[] = [
  "idle", "talk", "happy", "laugh", "sad", "angry", "shocked",
  "think", "point", "wave", "shrug", "thumbsup", "celebrate", "nod",
];

type Brow = "neutral" | "angry" | "sad" | "raised";
type Mouth = "flat" | "smile" | "grin" | "frown" | "open" | "talk";
export type HandKind = "relaxed" | "open" | "point" | "fist" | "thumb";
interface Pose {
  armL: [number, number]; // [upper, fore] absolute angles from straight-down (rad)
  armR: [number, number];
  brow: Brow;
  mouth: Mouth;
  bob: number;    // vertical bob amplitude (× u)
  tilt: number;   // head tilt (rad)
  jump: number;   // whole-body vertical hop (× u), negative = up
  /** Spine lean (REAL-ANIMATION §4): shoulders shift laterally over the hips —
   *  forward = engaged (point), back = arched (celebrate), drop = slumped (sad). */
  spine: number;
  handL: HandKind;
  handR: HandKind;
}

const REST_L: [number, number] = [0.24, 0.16];
const REST_R: [number, number] = [0.24, 0.16];

/** Reaction → pose as a pure function of `t` (seconds), looping for the whole beat. */
export function reactionPose(name: ReactionName, t: number): Pose {
  const base: Pose = { armL: REST_L, armR: REST_R, brow: "neutral", mouth: "flat", bob: Math.sin(t * 1.7) * 0.35, tilt: 0, jump: 0, spine: 0, handL: "relaxed", handR: "relaxed" };
  switch (name) {
    case "talk":
      return { ...base, mouth: "talk", spine: 0.05 };
    case "happy":
      return { ...base, mouth: "smile", brow: "raised", armL: [0.5, 0.4], armR: [0.5, 0.4], handL: "open", handR: "open", spine: 0.04 };
    case "laugh":
      return { ...base, mouth: "grin", brow: "raised", bob: Math.abs(Math.sin(t * 7)) * 0.9, armL: [0.7, 0.9], armR: [0.7, 0.9], tilt: 0.12, spine: -0.12, handL: "open", handR: "open" };
    case "sad":
      return { ...base, mouth: "frown", brow: "sad", bob: Math.sin(t * 1.1) * 0.2, tilt: 0.18, armL: [0.12, 0.1], armR: [0.12, 0.1], spine: 0.14 }; // slump forward
    case "angry":
      return { ...base, mouth: "frown", brow: "angry", armL: [0.35, 1.5], armR: [0.35, 1.5], handL: "fist", handR: "fist", spine: 0.1 };
    case "shocked":
      return { ...base, mouth: "open", brow: "raised", armL: [0.9, 1.1], armR: [0.9, 1.1], handL: "open", handR: "open", spine: -0.14 }; // recoil
    case "think":
      return { ...base, mouth: "flat", brow: "raised", tilt: 0.14, armR: [1.4, 2.3], armL: [0.2, 0.14], handR: "fist", spine: -0.08 }; // hand to chin, lean back
    case "point":
      return { ...base, mouth: "talk", armR: [1.45, 1.45], armL: [0.2, 0.14], handR: "point", spine: 0.12 }; // lean INTO the point
    case "wave": {
      const w = Math.sin(t * 6) * 0.35;
      return { ...base, mouth: "smile", brow: "raised", armR: [2.5, 2.5 + w], armL: [0.2, 0.14], handR: "open", spine: 0.04 };
    }
    case "shrug":
      return { ...base, mouth: "flat", brow: "raised", armL: [1.1, 2.0], armR: [1.1, 2.0], tilt: 0.08, handL: "open", handR: "open" };
    case "thumbsup":
      return { ...base, mouth: "smile", brow: "raised", armR: [0.7, 2.7], armL: [0.2, 0.14], handR: "thumb", spine: 0.05 };
    case "celebrate": {
      const j = Math.abs(Math.sin(t * 4));
      return { ...base, mouth: "grin", brow: "raised", armL: [2.7, 2.7], armR: [2.7, 2.7], jump: -j * 1.6, bob: 0, spine: -0.1, handL: "open", handR: "open" }; // arch back
    }
    case "nod":
      return { ...base, mouth: "smile", tilt: Math.sin(t * 3.2) * 0.14, spine: 0.05 };
    case "idle":
    default:
      return base;
  }
}

/** One key of a performance-acting timeline (REAL-ANIMATION §1). */
export interface ActingKeyRef { at: number; reaction: ReactionName }

const lerpN = (a: number, b: number, u: number) => a + (b - a) * u;
const easeOutQ = (u: number) => 1 - Math.pow(1 - Math.max(0, Math.min(1, u)), 5);

/** Blend two poses: numeric channels interpolate; face/hand channels cut at midpoint. */
function blendPose(a: Pose, b: Pose, u: number): Pose {
  return {
    armL: [lerpN(a.armL[0], b.armL[0], u), lerpN(a.armL[1], b.armL[1], u)],
    armR: [lerpN(a.armR[0], b.armR[0], u), lerpN(a.armR[1], b.armR[1], u)],
    brow: u < 0.5 ? a.brow : b.brow,
    mouth: u < 0.5 ? a.mouth : b.mouth,
    bob: lerpN(a.bob, b.bob, u),
    tilt: lerpN(a.tilt, b.tilt, u),
    jump: lerpN(a.jump, b.jump, u),
    spine: lerpN(a.spine, b.spine, u),
    handL: u < 0.5 ? a.handL : b.handL,
    handR: u < 0.5 ? a.handR : b.handR,
  };
}

const POSE_BLEND = 0.3; // seconds to settle into each new gesture

/**
 * Resolve the pose at time `t` from an acting timeline: each gesture runs on its
 * own local clock (so its entrance dynamics replay), and transitions BLEND over
 * ~0.3s — pose-to-pose animation with moving holds, not pose snapping.
 */
export function actedPose(acting: ActingKeyRef[] | undefined, fallback: ReactionName, t: number): Pose {
  if (!acting || acting.length === 0) return reactionPose(fallback, t);
  let idx = 0;
  for (let i = 0; i < acting.length; i++) if (acting[i].at <= t) idx = i;
  const cur = acting[idx];
  const pose = reactionPose(cur.reaction, Math.max(0, t - cur.at));
  if (idx === 0) return pose;
  const u = easeOutQ((t - cur.at) / POSE_BLEND);
  if (u >= 1) return pose;
  const prev = acting[idx - 1];
  const prevPose = reactionPose(prev.reaction, Math.max(0, t - prev.at));
  return blendPose(prevPose, pose, u);
}

// ── VISEMES (REAL-ANIMATION §5) ──────────────────────────────────────────────
// Syllable-approximated lip-sync, pure f(t): the spoken line is split into
// vowel-group "syllables", each mapped to a cartoon mouth shape (A open,
// E wide, O round, M lip-closure), spread across the speech window. No audio
// analysis, no per-frame cost — the same classic trick paper animators used.

export type Viseme = "rest" | "M" | "A" | "E" | "O";
export interface Speech { text: string; start: number; end: number }

/** Split a line into weighted mouth-shape beats (exported for tests). */
export function deriveVisemes(text: string): { v: Viseme; w: number }[] {
  const out: { v: Viseme; w: number }[] = [];
  const words = text.toLowerCase().replace(/[^a-z\s']/g, " ").split(/\s+/).filter(Boolean);
  for (const word of words) {
    if (/^[mbp]/.test(word)) out.push({ v: "M", w: 0.5 }); // lips close on m/b/p attacks
    let groups: string[] = word.match(/[aeiouy]+/g) ?? [];
    // Silent final e: only when the LAST vowel group is a lone "e" at word end
    // ("make" → 1 beat) — pronounced endings keep theirs ("movie", "coffee").
    if (groups.length > 1 && groups[groups.length - 1] === "e" && /e$/.test(word)) groups = groups.slice(0, -1);
    if (groups.length === 0) groups = ["e"]; // vowel-less word still gets a beat
    for (const g of groups) out.push({ v: g[0] === "a" ? "A" : g[0] === "o" || g[0] === "u" ? "O" : "E", w: 1 });
    out.push({ v: "rest", w: 0.35 }); // inter-word gap
  }
  if (out.length) out[out.length - 1] = { v: "rest", w: 0.6 }; // trail off at the end
  return out;
}

/** Mouth shape at `t` for a spoken line. Outside the window the mouth RESTS —
 *  a character stops flapping once the line is delivered. Returns undefined
 *  when the text has no Latin syllables ("2 + 2 = 4", non-Latin scripts) so
 *  the caller can fall back to the generic flap instead of a dead mouth. */
export function visemeAt(speech: Speech, t: number): Viseme | undefined {
  const seq = deriveVisemes(speech.text);
  if (!seq.length) return undefined;
  if (t < speech.start || t >= speech.end || speech.end <= speech.start) return "rest";
  const total = seq.reduce((s, k) => s + k.w, 0);
  const target = ((t - speech.start) / (speech.end - speech.start)) * total;
  let acc = 0;
  for (const k of seq) { acc += k.w; if (target < acc) return k.v; }
  return "rest";
}

// forward kinematics: from a joint, two segments at absolute angles (from down).
function chain(x0: number, y0: number, l1: number, a1: number, l2: number, a2: number, side: -1 | 1) {
  const ex = x0 + side * l1 * Math.sin(a1);
  const ey = y0 + l1 * Math.cos(a1);
  const hx = ex + side * l2 * Math.sin(a2);
  const hy = ey + l2 * Math.cos(a2);
  return { ex, ey, hx, hy };
}

function face(cx: number, cy: number, r: number, pose: Pose, spec: CharacterSpec, t: number, ink: string, skin: string, vis?: Viseme) {
  const eyeDx = r * 0.42;
  const eyeY = cy - r * 0.1;
  const blink = (t % 3.4) > 3.24 ? 0.12 : 1; // periodic blink
  // GAZE: eyes look ahead (toward the facing direction / conversation partner)
  // and scan slowly with an occasional saccade — living eyes, not painted dots.
  // Local +x is "forward" (the root transform flips the whole figure).
  const gazeX = r * (0.055 + 0.02 * Math.sin(t * 0.7)) + (Math.sin(t * 0.23) > 0.94 ? r * 0.02 : 0);
  const gazeY = r * 0.012 * Math.sin(t * 0.5);
  // Talking mouth: with a Speech attached the shapes follow the LINE's syllables
  // (visemes); without one, fall back to the generic sine flap.
  const talkOpen: Mouth | Viseme = pose.mouth === "talk"
    ? (vis ?? (Math.sin(t * 12) > 0 ? "open" : "flat"))
    : pose.mouth;
  // brows
  const browY = cy - r * 0.5;
  const bl = (dir: number) => {
    const a = pose.brow === "angry" ? dir * 0.32 : pose.brow === "sad" ? -dir * 0.32 : 0;
    const yo = pose.brow === "raised" ? -r * 0.16 : 0;
    return { x1: cx + dir * (eyeDx + r * 0.22), y1: browY + yo + a * r, x2: cx + dir * (eyeDx - r * 0.18), y2: browY + yo - a * r };
  };
  const b1 = bl(-1), b2 = bl(1);
  // mouth path
  let mouthEl: React.ReactNode;
  const my = cy + r * 0.42, mw = r * 0.5;
  if (talkOpen === "open" || talkOpen === "A") mouthEl = <ellipse cx={cx} cy={my} rx={mw * 0.5} ry={r * 0.24} fill={ink} />;
  else if (talkOpen === "E") mouthEl = <g><ellipse cx={cx} cy={my} rx={mw * 0.68} ry={r * 0.11} fill={ink} /><line x1={cx - mw * 0.45} y1={my} x2={cx + mw * 0.45} y2={my} stroke="#ffffff" strokeWidth={r * 0.05} opacity={0.85} /></g>;
  else if (talkOpen === "O") mouthEl = <ellipse cx={cx} cy={my} rx={r * 0.15} ry={r * 0.18} fill={ink} />;
  else if (talkOpen === "M") mouthEl = <line x1={cx - mw * 0.5} y1={my} x2={cx + mw * 0.5} y2={my} stroke={ink} strokeWidth={r * 0.16} strokeLinecap="round" />;
  else if (talkOpen === "smile") mouthEl = <path d={`M ${cx - mw} ${my - r * 0.05} Q ${cx} ${my + r * 0.4} ${cx + mw} ${my - r * 0.05}`} fill="none" stroke={ink} strokeWidth={r * 0.12} strokeLinecap="round" />;
  else if (talkOpen === "grin") mouthEl = <path d={`M ${cx - mw} ${my - r * 0.05} Q ${cx} ${my + r * 0.55} ${cx + mw} ${my - r * 0.05} Z`} fill={ink} />;
  else if (talkOpen === "frown") mouthEl = <path d={`M ${cx - mw} ${my + r * 0.2} Q ${cx} ${my - r * 0.25} ${cx + mw} ${my + r * 0.2}`} fill="none" stroke={ink} strokeWidth={r * 0.12} strokeLinecap="round" />;
  else mouthEl = <line x1={cx - mw * 0.7} y1={my} x2={cx + mw * 0.7} y2={my} stroke={ink} strokeWidth={r * 0.12} strokeLinecap="round" />;
  return (
    <g>
      {/* freckles / pimple */}
      {spec.face?.freckles && [-1, 1].map((d) => <circle key={d} cx={cx + d * r * 0.55} cy={cy + r * 0.28} r={r * 0.05} fill="#b9724e" opacity={0.6} />)}
      {spec.face?.pimple && <circle cx={cx + r * 0.4} cy={cy + r * 0.05} r={r * 0.08} fill="#d0584f" />}
      {/* eyes */}
      <ellipse cx={cx - eyeDx + gazeX} cy={eyeY + gazeY} rx={r * 0.12} ry={r * 0.14 * blink} fill={ink} />
      <ellipse cx={cx + eyeDx + gazeX} cy={eyeY + gazeY} rx={r * 0.12} ry={r * 0.14 * blink} fill={ink} />
      {/* brows */}
      <line x1={b1.x1} y1={b1.y1} x2={b1.x2} y2={b1.y2} stroke={ink} strokeWidth={r * 0.1} strokeLinecap="round" />
      <line x1={b2.x1} y1={b2.y1} x2={b2.x2} y2={b2.y2} stroke={ink} strokeWidth={r * 0.1} strokeLinecap="round" />
      {mouthEl}
      {/* glasses */}
      {spec.face?.glasses && (
        <g fill="none" stroke={ink} strokeWidth={r * 0.08}>
          <circle cx={cx - eyeDx} cy={eyeY} r={r * 0.26} />
          <circle cx={cx + eyeDx} cy={eyeY} r={r * 0.26} />
          <line x1={cx - eyeDx + r * 0.26} y1={eyeY} x2={cx + eyeDx - r * 0.26} y2={eyeY} />
        </g>
      )}
    </g>
  );
}

function hair(cx: number, cy: number, r: number, spec: CharacterSpec, color: string) {
  if (spec.hair === "bald") return null;
  const c = color;
  if (spec.hair === "curly") return (
    <g fill={c}>{[-1, -0.5, 0, 0.5, 1].map((d, i) => <circle key={i} cx={cx + d * r * 0.7} cy={cy - r * 0.85} r={r * 0.34} />)}</g>
  );
  if (spec.hair === "long") return <path d={`M ${cx - r * 1.05} ${cy + r * 0.6} Q ${cx - r * 1.15} ${cy - r * 1.2} ${cx} ${cy - r * 1.2} Q ${cx + r * 1.15} ${cy - r * 1.2} ${cx + r * 1.05} ${cy + r * 0.6} L ${cx + r * 0.8} ${cy + r * 0.5} Q ${cx} ${cy - r * 0.7} ${cx - r * 0.8} ${cy + r * 0.5} Z`} fill={c} />;
  if (spec.hair === "hairy") return <path d={`M ${cx - r} ${cy - r * 0.2} Q ${cx - r * 1.1} ${cy - r * 1.3} ${cx} ${cy - r * 1.3} Q ${cx + r * 1.1} ${cy - r * 1.3} ${cx + r} ${cy - r * 0.2} Q ${cx + r * 0.6} ${cy - r * 0.9} ${cx} ${cy - r * 0.85} Q ${cx - r * 0.6} ${cy - r * 0.9} ${cx - r} ${cy - r * 0.2} Z`} fill={c} />;
  // short (default)
  return <path d={`M ${cx - r * 0.98} ${cy - r * 0.15} Q ${cx - r} ${cy - r * 1.15} ${cx} ${cy - r * 1.15} Q ${cx + r} ${cy - r * 1.15} ${cx + r * 0.98} ${cy - r * 0.15} Q ${cx + r * 0.5} ${cy - r * 0.75} ${cx} ${cy - r * 0.72} Q ${cx - r * 0.5} ${cy - r * 0.75} ${cx - r * 0.98} ${cy - r * 0.15} Z`} fill={c} />;
}

export interface CharacterProps { spec: CharacterSpec; reaction: ReactionName; box: Box; theme: Theme; t: number; facing?: -1 | 1; seed?: number; acting?: ActingKeyRef[]; speech?: Speech; /** Stage floor line (abs y). Pass when the box itself moves (walk-in bob) so the shadow stays ON the floor. */ groundY?: number }

export function CharacterActor({ spec, reaction, box, theme, t, facing = 1, seed = 0, acting, speech, groundY }: CharacterProps) {
  const P = theme.palette;
  const ink = theme.dark ? "#0f1319" : "#1a2230";
  const skin = spec.skinTone ?? "#e8b48c";
  const top = spec.outfit?.top ?? P.primary;
  const heightK = spec.height === "tall" ? 1.08 : spec.height === "short" ? 0.9 : 1;
  const buildK = spec.build === "fat" ? 1.5 : spec.build === "slim" ? 0.7 : 1;
  const ageK = spec.age === "child" ? 0.8 : spec.age === "old" ? 0.98 : 1;

  const fh = box.h * 0.82 * heightK * ageK;
  // Performance acting: an acting timeline makes the character perform each
  // phrase; without one, the single reaction plays as before.
  const pose = actedPose(acting, reaction, t);
  // Follow-through (Disney #5): arms lag the body by a breath, so gestures whip
  // and settle instead of tracking the torso rigidly.
  const armPose = actedPose(acting, reaction, lagged(t, 0.07));
  const cxo = box.cx;
  const feetY = box.y + box.h * 0.97 + pose.jump * (fh * 0.02) + pose.bob * (fh * 0.008);
  const headR = fh * (spec.age === "child" ? 0.13 : 0.105);
  const headCy = feetY - fh + headR;
  const neckY = headCy + headR * 0.9;
  const shoulderY = neckY + fh * 0.03;
  const hipY = shoulderY + fh * 0.30;
  const shoulderHalf = fh * 0.085 * buildK;
  const upper = fh * 0.17, fore = fh * 0.17;
  const thigh = fh * 0.21, shin = fh * 0.21;
  const av = spec.family === "avatar";
  const lw = fh * (av ? 0.052 : 0.028); // avatar limbs are fuller (capsules)
  const legColor = av ? "#3b4453" : ink; // avatar wears trousers; stick is line-art
  const armColor = av ? skin : ink;      // avatar arms are skin; stick is line-art

  // Spine (REAL-ANIMATION §4): the shoulders shift over the hips and the head
  // LEADS the lean slightly — bodies engage, arch, and slump instead of
  // standing like columns. Local +x is "forward" (the root flip handles sides).
  const spineDx = pose.spine * fh * 0.5;
  const shCx = cxo + spineDx;
  const headCx = cxo + spineDx * 1.35;
  const shL = { x: shCx - shoulderHalf, y: shoulderY };
  const shR = { x: shCx + shoulderHalf, y: shoulderY };
  const hipL = { x: cxo - shoulderHalf * 0.8, y: hipY };
  const hipR = { x: cxo + shoulderHalf * 0.8, y: hipY };
  const aL = chain(shL.x, shL.y, upper, armPose.armL[0], fore, armPose.armL[1], -1);
  const aR = chain(shR.x, shR.y, upper, armPose.armR[0], fore, armPose.armR[1], 1);
  const walk = Math.sin(t * 3) * 0.12;
  const lL = chain(hipL.x, hipL.y, thigh, 0.08 + walk, shin, 0.06 + walk, -1);
  const lR = chain(hipR.x, hipR.y, thigh, 0.08 - walk, shin, 0.06 - walk, 1);

  // Squash & stretch (Disney #1): during a jump the body stretches while rising
  // and squashes on landing, volume-preserved, anchored at the feet. The jump
  // velocity is the derivative of the reaction's jump track (celebrate uses
  // |sin(4t)| → v ∝ cos(4t)·sign(sin(4t))).
  const jumpV = pose.jump !== 0 ? Math.cos(t * 4) * Math.sign(Math.sin(t * 4) || 1) : 0;
  const { sx: ssx, sy: ssy } = squashStretch(-jumpV, 0.12);
  // Secondary action (Disney #8): a living idle — breath in the torso scale and
  // a phase-seeded sway so a cast never moves in lockstep. Entrance (start of the
  // beat) settles in with a spring instead of popping.
  const enter = settleScale(t, 0.5, 0.28);
  const swayA = sway(t, seed, reaction === "idle" || reaction === "talk" ? 0.022 : 0.01);
  const lifeSy = ssy * breathe(t, 0.5, reaction === "idle" ? 0.012 : 0.006) * enter;
  const lifeSx = ssx * enter;

  // Stick = single line-art stroke; avatar = a thicker capsule with a dark outline.
  const limb = (a: { ex: number; ey: number; hx: number; hy: number }, j: { x: number; y: number }, color: string) => {
    const d = `M ${j.x} ${j.y} L ${a.ex} ${a.ey} L ${a.hx} ${a.hy}`;
    return (
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {av && <path d={d} stroke={ink} strokeWidth={lw + fh * 0.012} />}
        <path d={d} stroke={color} strokeWidth={lw} />
      </g>
    );
  };
  const handSize = lw * (av ? 0.85 : 1.1);
  const midY = (shoulderY + hipY) / 2;
  const bulge = spineDx * 0.55;

  // LIGHT PASS (REAL-ANIMATION §5): a soft CONTACT SHADOW anchors the figure to
  // the ground (it stays on the floor and shrinks/fades as the body lifts on a
  // jump — the classic altitude cue), and a RIM LIGHT kisses the sun side of the
  // head + torso. The stage sun sits screen-right; `facing` mirrors the body, so
  // `dir` compensates to keep the rim world-consistent. All pure f(t), $0.
  const floorY = groundY ?? box.y + box.h * 0.97;
  const lift = Math.max(0, floorY - feetY);
  const liftK = Math.min(1, lift / (fh * 0.06));
  const dir = facing;
  const rimSweep = dir === 1 ? 1 : 0;

  return (
    <g>
      <ellipse cx={cxo} cy={floorY + lw * 0.9} rx={fh * 0.17 * lifeSx * (1 - 0.35 * liftK)} ry={fh * 0.028 * (1 - 0.3 * liftK)} fill={ink} opacity={0.14 * (1 - 0.55 * liftK)} />
    <g transform={`translate(${cxo} ${feetY}) scale(${lifeSx.toFixed(4)} ${lifeSy.toFixed(4)}) rotate(${(swayA * 57.3).toFixed(2)}) translate(${-cxo} ${-feetY}) rotate(${pose.tilt * 6} ${cxo} ${headCy}) scale(${facing} 1) translate(${facing < 0 ? -2 * cxo : 0} 0)`}>
      {/* legs */}
      {limb(lL, hipL, legColor)}
      {limb(lR, hipR, legColor)}
      <circle cx={lL.hx} cy={lL.hy} r={lw * 0.9} fill={av ? "#2c333f" : ink} stroke={av ? ink : undefined} strokeWidth={lw * 0.4} />
      <circle cx={lR.hx} cy={lR.hy} r={lw * 0.9} fill={av ? "#2c333f" : ink} stroke={av ? ink : undefined} strokeWidth={lw * 0.4} />
      {/* torso: a CURVED trunk — sides bow through the spine lean */}
      {av ? (
        <path
          d={`M ${shL.x - lw * 0.9 + lw} ${shoulderY - lw * 0.6}
              L ${shR.x + lw * 0.9 - lw} ${shoulderY - lw * 0.6}
              Q ${shR.x + lw * 0.9} ${shoulderY - lw * 0.6} ${shR.x + lw * 0.9} ${shoulderY + lw * 0.4}
              Q ${(shR.x + hipR.x) / 2 + lw * 0.9 + bulge} ${midY} ${hipR.x + lw * 0.9} ${hipY + lw * 1.2}
              Q ${hipR.x + lw * 0.9} ${hipY + lw * 2} ${hipR.x} ${hipY + lw * 2}
              L ${hipL.x} ${hipY + lw * 2}
              Q ${hipL.x - lw * 0.9} ${hipY + lw * 2} ${hipL.x - lw * 0.9} ${hipY + lw * 1.2}
              Q ${(shL.x + hipL.x) / 2 - lw * 0.9 + bulge} ${midY} ${shL.x - lw * 0.9} ${shoulderY + lw * 0.4}
              Q ${shL.x - lw * 0.9} ${shoulderY - lw * 0.6} ${shL.x - lw * 0.9 + lw} ${shoulderY - lw * 0.6} Z`}
          fill={top} stroke={ink} strokeWidth={lw * 0.5} strokeLinejoin="round"
        />
      ) : (
        <path
          d={`M ${shL.x - lw} ${shoulderY} L ${shR.x + lw} ${shoulderY}
              Q ${(shR.x + hipR.x) / 2 + lw + bulge} ${midY} ${hipR.x + lw} ${hipY}
              L ${hipL.x - lw} ${hipY}
              Q ${(shL.x + hipL.x) / 2 - lw + bulge} ${midY} ${shL.x - lw} ${shoulderY} Z`}
          fill={top} stroke={ink} strokeWidth={lw * 0.7} strokeLinejoin="round"
        />
      )}
      {/* arms + HANDS (relaxed / open / point / fist / thumb, aimed by the forearm) */}
      {limb(aL, shL, armColor)}
      {limb(aR, shR, armColor)}
      <Hand x={aL.hx} y={aL.hy} ex={aL.ex} ey={aL.ey} kind={pose.handL} size={handSize} skin={skin} ink={ink} lw={lw} />
      <Hand x={aR.hx} y={aR.hy} ex={aR.ex} ey={aR.ey} kind={pose.handR} size={handSize} skin={skin} ink={ink} lw={lw} />
      {/* neck + head (the head leads the lean) */}
      {av
        ? <><line x1={headCx} y1={neckY} x2={shCx} y2={shoulderY + lw} stroke={ink} strokeWidth={lw * 1.5 + fh * 0.012} strokeLinecap="round" /><line x1={headCx} y1={neckY} x2={shCx} y2={shoulderY + lw} stroke={skin} strokeWidth={lw * 1.5} strokeLinecap="round" /></>
        : <line x1={headCx} y1={neckY} x2={shCx} y2={shoulderY} stroke={ink} strokeWidth={lw} />}
      <circle cx={headCx} cy={headCy} r={headR} fill={skin} stroke={ink} strokeWidth={lw * 0.7} />
      {av && [-1, 1].map((d) => <ellipse key={`bl${d}`} cx={headCx + d * headR * 0.56} cy={headCy + headR * 0.34} rx={headR * 0.2} ry={headR * 0.13} fill="#ff9d9d" opacity={0.5} />)}
      {hair(headCx, headCy, headR, spec, spec.hairColor ?? "#33291f")}
      {(spec.facialHair === "beard" || spec.facialHair === "stubble") &&
        <path d={`M ${headCx - headR * 0.8} ${headCy + headR * 0.2} Q ${headCx} ${headCy + headR * 1.15} ${headCx + headR * 0.8} ${headCy + headR * 0.2}`} fill={spec.facialHair === "stubble" ? "none" : (spec.hairColor ?? "#33291f")} stroke={spec.hairColor ?? "#33291f"} strokeWidth={headR * (spec.facialHair === "stubble" ? 0.12 : 0.2)} opacity={spec.facialHair === "stubble" ? 0.5 : 1} />}
      {spec.facialHair === "moustache" && <path d={`M ${headCx - headR * 0.5} ${headCy + headR * 0.3} Q ${headCx} ${headCy + headR * 0.5} ${headCx + headR * 0.5} ${headCy + headR * 0.3}`} fill="none" stroke={spec.hairColor ?? "#33291f"} strokeWidth={headR * 0.16} strokeLinecap="round" />}
      {face(headCx, headCy, headR, pose, spec, t, ink, skin, speech ? visemeAt(speech, t) : undefined)}
      {/* rim light — sun-side cheek + torso edge */}
      <path d={`M ${headCx + dir * headR * 0.74} ${headCy - headR * 0.5} A ${headR * 0.92} ${headR * 0.92} 0 0 ${rimSweep} ${headCx + dir * headR * 0.9} ${headCy + headR * 0.26}`} fill="none" stroke="#ffffff" strokeWidth={lw * (av ? 0.55 : 0.7)} strokeLinecap="round" opacity={0.32} />
      <path d={`M ${shCx + dir * shoulderHalf + (dir === 1 ? lw * 0.7 : -lw * 0.7)} ${shoulderY + lw * 1.2} Q ${(shCx + cxo) / 2 + dir * shoulderHalf * 0.95 + bulge} ${midY} ${cxo + dir * shoulderHalf * 0.82} ${hipY - lw * 0.4}`} fill="none" stroke="#ffffff" strokeWidth={lw * 0.5} strokeLinecap="round" opacity={0.18} />
    </g>
    </g>
  );
}

/** Cartoon hand (REAL-ANIMATION §4): palm + fingers, aimed along the forearm. */
const Hand: React.FC<{ x: number; y: number; ex: number; ey: number; kind: HandKind; size: number; skin: string; ink: string; lw: number }> = ({
  x, y, ex, ey, kind, size, skin, ink, lw,
}) => {
  const ang = (Math.atan2(y - ey, x - ex) * 180) / Math.PI;
  const s = size;
  const finger = (fx: number, fy: number, len: number, a: number, key: string) => {
    const rad = (a * Math.PI) / 180;
    const tx = fx + Math.cos(rad) * len;
    const ty = fy + Math.sin(rad) * len;
    return (
      <g key={key} strokeLinecap="round" fill="none">
        <line x1={fx} y1={fy} x2={tx} y2={ty} stroke={ink} strokeWidth={s * 0.62} />
        <line x1={fx} y1={fy} x2={tx} y2={ty} stroke={skin} strokeWidth={s * 0.42} />
      </g>
    );
  };
  return (
    <g transform={`rotate(${ang.toFixed(1)} ${x} ${y})`}>
      {kind === "open" && [-26, 0, 26].map((a, i) => finger(x, y, s * 1.5, a, `f${i}`))}
      {kind === "point" && finger(x, y, s * 1.9, 0, "idx")}
      {kind === "thumb" && finger(x, y, s * 1.5, -80, "th")}
      <circle cx={x} cy={y} r={kind === "fist" ? s * 1.15 : s} fill={skin} stroke={ink} strokeWidth={lw * 0.5} />
      {kind === "fist" && <path d={`M ${x - s * 0.5} ${y - s * 0.45} Q ${x + s * 0.25} ${y - s * 0.7} ${x + s * 0.6} ${y - s * 0.2}`} fill="none" stroke={ink} strokeWidth={s * 0.3} strokeLinecap="round" />}
      {kind === "relaxed" && <path d={`M ${x + s * 0.15} ${y - s * 0.5} Q ${x + s * 0.55} ${y} ${x + s * 0.15} ${y + s * 0.5}`} fill="none" stroke={ink} strokeWidth={s * 0.22} strokeLinecap="round" opacity={0.55} />}
    </g>
  );
};
